/**
 * CR450: Flyer Image Capture Runner Panel
 * 
 * Shown in FlyerPipelineStepsPanel when the batch run is in IMAGE_CAPTURE mode.
 * Provides:
 *   - Latest capture attempt status
 *   - "Run Capture Now" button
 *   - Auto-run toggle
 *   - Clear indication that S1.0 + S1.1 are skipped
 */

import React, { useState, useEffect, useCallback } from "react";
import { Camera, Play, CheckCircle2, XCircle, Loader2, RefreshCw, Bug, ExternalLink, Copy, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ImageCaptureDiagnosticsBanner } from "./ImageCaptureDiagnosticsBanner";
import { ImageCaptureDebugModal } from "./ImageCaptureDebugModal";
import type { OcrEngineValue } from "@/lib/batch-runs/ocrEngine";

interface CaptureAttempt {
  id: string;
  status: string;
  mode: string;
  merchants_total: number;
  merchants_succeeded: number;
  merchants_failed: number;
  pages_total: number;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

interface FlyerImageCapturePanelProps {
  batchRunId: string;
  ocrEngine?: OcrEngineValue;
  onCaptureComplete?: () => void;
  onRefresh?: () => void;
  onS1Complete?: () => void;
  onRunCaptureParent?: (trace: any) => void;
}

const ATTEMPT_STATUS_CONFIG: Record<string, {
  label: string;
  icon: React.ReactNode;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
}> = {
  queued: { label: "Queued", icon: <Loader2 className="h-3 w-3 animate-spin" />, badgeVariant: "secondary" },
  running: { label: "Running", icon: <Loader2 className="h-3 w-3 animate-spin" />, badgeVariant: "default" },
  complete: { label: "Complete", icon: <CheckCircle2 className="h-3 w-3" />, badgeVariant: "outline" },
  failed: { label: "Failed", icon: <XCircle className="h-3 w-3" />, badgeVariant: "destructive" },
};

export function FlyerImageCapturePanel({
  batchRunId,
  ocrEngine = "tesseract",
  onCaptureComplete,
  onRefresh,
  onS1Complete,
  onRunCaptureParent,
}: FlyerImageCapturePanelProps) {
  const { toast } = useToast();
  const [latestAttempt, setLatestAttempt] = useState<CaptureAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [selectedMerchants, setSelectedMerchants] = useState<number>(0);
  // CR494: Manifest completeness
  const [manifestStats, setManifestStats] = useState<{
    total: number;
    complete: number;
    incomplete: number;
    failed: number;
    expectedPages: number;
    registryPages: number;
    registryPagesRaw: number;
    registryUsingFallback: boolean;
  } | null>(null);
  const [pageSurfaceStats, setPageSurfaceStats] = useState<{
    truthRenderedPages: number;
    registryPagesRaw: number;
    registryPagesEffective: number;
    attemptPagesTotal: number;
    manifestReceivedPages: number;
    registryUsingFallback: boolean;
  } | null>(null);

  const fetchLatestAttempt = useCallback(async () => {
    const [attemptRes, manifestRes, registryRes, truthRenderedRes, batchRunRes] = await Promise.all([
      supabase
        .from("flyer_capture_attempts")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("flyer_image_capture_manifests" as any)
        .select("id, merchant_source_id, status, page_count_expected, page_count_found, page_count_received, created_at")
        .eq("batch_run_id", batchRunId),
      supabase
        .from("batch_run_page_assets")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .not("bucket_id", "is", null)
        .not("object_path", "is", null),
      supabase
        .from("cr075_flyer_page_truth" as any)
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .eq("rendered", true)
        .not("render_storage_path", "is", null),
      supabase
        .from("cr075_batch_runs")
        .select("merchants_in_scope, config_json")
        .eq("id", batchRunId)
        .maybeSingle(),
    ]);

    if (!attemptRes.error && attemptRes.data) {
      setLatestAttempt(attemptRes.data as CaptureAttempt);
    } else {
      setLatestAttempt(null);
    }

    if (!batchRunRes.error && batchRunRes.data) {
      const runCfg = (batchRunRes.data.config_json ?? {}) as Record<string, unknown>;
      const merchantsFromConfig = Array.isArray(runCfg.merchant_uuids)
        ? runCfg.merchant_uuids.length
        : Array.isArray(runCfg.merchants)
          ? runCfg.merchants.length
          : Array.isArray(runCfg.merchant_ids)
            ? runCfg.merchant_ids.length
            : 0;
      const selectedCount = merchantsFromConfig > 0
        ? merchantsFromConfig
        : Number(batchRunRes.data.merchants_in_scope ?? 0);
      setSelectedMerchants(Number.isFinite(selectedCount) ? selectedCount : 0);
    } else {
      setSelectedMerchants(0);
    }

    if (registryRes.error) {
      console.warn("[FlyerImageCapturePanel] Registry count failed:", registryRes.error.message);
    }
    if (truthRenderedRes.error) {
      console.warn("[FlyerImageCapturePanel] Truth rendered count failed:", truthRenderedRes.error.message);
    }

    let registryCount = registryRes.count ?? 0;
    if (registryCount === 0 && !registryRes.error) {
      const registryRowsRes = await supabase
        .from("batch_run_page_assets")
        .select("page_number")
        .eq("batch_run_id", batchRunId)
        .limit(5000);
      if (registryRowsRes.error) {
        console.warn("[FlyerImageCapturePanel] Registry row fallback failed:", registryRowsRes.error.message);
      } else {
        registryCount = registryRowsRes.data?.length ?? 0;
      }
    }
    const truthRenderedCount = truthRenderedRes.count ?? 0;
    const attemptPagesTotal = attemptRes.data?.pages_total ?? 0;
    const rawRegistryPages = registryCount;

    // CR494: Compute manifest stats
    const manifests = (manifestRes.data as any[]) ?? [];
    // CR632: Deduplicate by merchant_source_id to avoid inflated expected page totals
    // when a merchant posts multiple COMPLETE manifests for the same run.
    const latestManifestBySource = new Map<string, any>();
    for (const manifest of manifests) {
      const sourceId = manifest?.merchant_source_id || manifest?.id;
      const existing = latestManifestBySource.get(sourceId);
      if (!existing) {
        latestManifestBySource.set(sourceId, manifest);
        continue;
      }
      const existingTs = new Date(existing.created_at ?? 0).getTime();
      const nextTs = new Date(manifest.created_at ?? 0).getTime();
      if (Number.isFinite(nextTs) && nextTs >= existingTs) {
        latestManifestBySource.set(sourceId, manifest);
      }
    }
    const dedupedManifests = Array.from(latestManifestBySource.values());
    const manifestReceivedPages = dedupedManifests.reduce(
      (s: number, m: any) => s + (m.page_count_received ?? m.page_count_found ?? 0),
      0,
    );
    const effectiveRegistryPages = Math.max(
      rawRegistryPages,
      truthRenderedCount,
      manifestReceivedPages,
      attemptPagesTotal,
    );
    const registryUsingFallback = rawRegistryPages === 0 && effectiveRegistryPages > 0;

    setPageSurfaceStats({
      truthRenderedPages: truthRenderedCount,
      registryPagesRaw: rawRegistryPages,
      registryPagesEffective: effectiveRegistryPages,
      attemptPagesTotal,
      manifestReceivedPages,
      registryUsingFallback,
    });

    setManifestStats({
      total: dedupedManifests.length,
      complete: dedupedManifests.filter((m) => m.status === 'COMPLETE').length,
      incomplete: dedupedManifests.filter((m) => m.status === 'CAPTURE_INCOMPLETE').length,
      failed: dedupedManifests.filter((m) => m.status === 'FAILED').length,
      expectedPages: dedupedManifests.reduce((s: number, m: any) => s + (m.page_count_expected ?? 0), 0),
      registryPages: effectiveRegistryPages,
      registryPagesRaw: rawRegistryPages,
      registryUsingFallback,
    });

    setLoading(false);
  }, [batchRunId]);

  useEffect(() => {
    fetchLatestAttempt();
  }, [fetchLatestAttempt]);

  // Poll while running
  useEffect(() => {
    if (latestAttempt?.status === "running" || latestAttempt?.status === "queued") {
      const interval = setInterval(fetchLatestAttempt, 3000);
      return () => clearInterval(interval);
    }
  }, [latestAttempt?.status, fetchLatestAttempt]);

  // Notify parent when attempt transitions to complete
  const prevStatusRef = React.useRef<string | null>(null);

  useEffect(() => {
    const currentStatus = latestAttempt?.status;
    const isComplete = currentStatus === "complete";
    const wasRunning = prevStatusRef.current === "running" || prevStatusRef.current === "queued";

    if (isComplete && wasRunning) {
      onCaptureComplete?.();
    }

    prevStatusRef.current = currentStatus || null;
  }, [latestAttempt?.status, onCaptureComplete]);

  const handleRunCapture = async (mode: "manual" | "auto" = "manual") => {
    console.log(`[ImageCapturePanel] handleRunCapture entry. mode=${mode}, batchRunId=${batchRunId}`);
    setRunning(true);
    try {
      const response = await supabase.functions.invoke("batch-run-flyer-image-capture", {
        body: {
          batch_run_id: batchRunId,
          mode,
          force: false,
        },
      });

      if (response.error) throw response.error;

      const result = response.data;

      // Store trace in parent
      if (onRunCaptureParent) {
        onRunCaptureParent({
          capturedAt: new Date().toISOString(),
          request: { batch_run_id: batchRunId, mode, force: false },
          response: result,
          _source: "FlyerImageCapturePanel.handleRunCapture"
        });
      }

      // Refresh attempt status
      await fetchLatestAttempt();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("[ImageCapture] Failed to invoke:", err);
      const rawMessage = err instanceof Error ? err.message : String(err ?? "Unknown error");
      const inVersionHistoryView =
        typeof window !== "undefined" &&
        /restore this version/i.test(document?.body?.innerText ?? "");
      const likelyHistoryModeFailure =
        inVersionHistoryView && /failed to send a request|fetch failed/i.test(rawMessage);
      toast({
        title: "Capture Failed",
        description: likelyHistoryModeFailure
          ? "This looks like a version-history preview. Click 'Back to latest' and retry capture."
          : rawMessage,
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const handleForceRerun = async () => {
    setRunning(true);
    try {
      const response = await supabase.functions.invoke("batch-run-flyer-image-capture", {
        body: {
          batch_run_id: batchRunId,
          mode: "manual",
          force: true,
        },
      });

      if (response.error) throw response.error;

      const result = response.data;
      if (onRunCaptureParent) {
        onRunCaptureParent({
          capturedAt: new Date().toISOString(),
          request: { batch_run_id: batchRunId, mode: "manual", force: true },
          response: result,
          _source: "FlyerImageCapturePanel.handleForceRerun"
        });
      }

      toast({
        title: "Force Rerun Started",
        description: "Cleared existing CAPTURE pages and re-registering...",
      });
      await fetchLatestAttempt();
      if (onRefresh) onRefresh();
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : String(err ?? "Unknown error");
      const inVersionHistoryView =
        typeof window !== "undefined" &&
        /restore this version/i.test(document?.body?.innerText ?? "");
      const likelyHistoryModeFailure =
        inVersionHistoryView && /failed to send a request|fetch failed/i.test(rawMessage);
      toast({
        title: "Force Rerun Failed",
        description: likelyHistoryModeFailure
          ? "This looks like a version-history preview. Click 'Back to latest' and retry."
          : rawMessage,
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const handleRetryFailed = async () => {
    setRunning(true);
    try {
      const response = await supabase.functions.invoke("batch-run-flyer-image-capture", {
        body: {
          batch_run_id: batchRunId,
          mode: "manual",
          force: false,
          retry_failed_only: true,
        },
      });

      if (response.error) throw response.error;

      const result = response.data;
      if (onRunCaptureParent) {
        onRunCaptureParent({
          capturedAt: new Date().toISOString(),
          request: { batch_run_id: batchRunId, mode: "manual", force: false, retry_failed_only: true },
          response: result,
          _source: "FlyerImageCapturePanel.handleRetryFailed"
        });
      }

      toast({
        title: "Retry Failed Started",
        description: "Queued failed capture jobs for retry.",
      });
      await fetchLatestAttempt();
      if (onRefresh) onRefresh();
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : String(err ?? "Unknown error");
      toast({
        title: "Retry Failed Failed",
        description: rawMessage,
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const handleForceStop = async () => {
    const confirmed = window.confirm(
      "Force stop this S1.0c run? This cancels all active capture jobs for the current batch."
    );
    if (!confirmed) return;

    setStopping(true);
    const requestBody = {
      action: "cancel_batch",
      batch_run_id: batchRunId,
    };
    try {
      const response = await supabase.functions.invoke("image-capture-watchdog", {
        body: requestBody,
      });

      if (response.error) throw response.error;

      if (onRunCaptureParent) {
        onRunCaptureParent({
          capturedAt: new Date().toISOString(),
          request: requestBody,
          response: response.data,
          _source: "FlyerImageCapturePanel.handleForceStop"
        });
      }

      const cancelled = Number(response.data?.cancelled ?? 0);
      toast({
        title: cancelled > 0 ? "Capture force-stopped" : "No active capture jobs",
        description:
          cancelled > 0
            ? `Cancelled ${cancelled} active capture job${cancelled === 1 ? "" : "s"}.`
            : "There were no RUNNING or QUEUED capture jobs to cancel.",
      });

      await fetchLatestAttempt();
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : String(err ?? "Unknown error");
      if (onRunCaptureParent) {
        onRunCaptureParent({
          capturedAt: new Date().toISOString(),
          request: requestBody,
          error: {
            name: err instanceof Error ? err.name : "InvokeError",
            message: rawMessage,
            stack: err instanceof Error ? err.stack : undefined,
          },
          _source: "FlyerImageCapturePanel.handleForceStop"
        });
      }
      toast({
        title: "Force Stop Failed",
        description: rawMessage,
        variant: "destructive",
      });
    } finally {
      setStopping(false);
    }
  };

  const statusCfg = latestAttempt
    ? (ATTEMPT_STATUS_CONFIG[latestAttempt.status] ?? ATTEMPT_STATUS_CONFIG.queued)
    : null;

  const isActivelyRunning =
    running ||
    latestAttempt?.status === "running" ||
    latestAttempt?.status === "queued";
  const canForceStop = isActivelyRunning || stopping;
  const displayedMerchantsFailed = latestAttempt
    ? Math.max(
      latestAttempt.merchants_failed,
      selectedMerchants > 0 ? selectedMerchants - latestAttempt.merchants_succeeded : 0,
    )
    : 0;

  const localLauncherBaseUrl = (import.meta.env.VITE_LOCAL_COMMAND_LAUNCH_URL ?? "terminal://run").replace(/\/+$/, "");

  const launchLocalCommand = useCallback(async (command: string, label: string) => {
    const commandUrl = `${localLauncherBaseUrl}?command=${encodeURIComponent(command)}`;

    if (typeof window !== "undefined") {
      window.open(commandUrl, "_blank", "noopener,noreferrer");
    }

    try {
      await navigator.clipboard.writeText(command);
    } catch {
      // Clipboard is best-effort; command is still attempted via terminal URL handler.
    }

    toast({
      title: `${label} launch requested`,
      description: `Command copied: ${command}`,
    });
  }, [localLauncherBaseUrl, toast]);

  const copyLocalCommand = useCallback(async (command: string, label: string) => {
    if (!navigator?.clipboard?.writeText) {
      toast({
        title: "Clipboard unavailable",
        description: "Your browser does not allow clipboard writes in this context.",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(command);
      toast({
        title: `${label} command copied`,
        description: command,
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err instanceof Error ? err.message : "Could not copy command to clipboard.",
        variant: "destructive",
      });
    }
  }, [toast]);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-primary" />
          <div>
            <h3 className="text-sm font-semibold">S1.0c: Image Capture</h3>
            <p className="text-xs text-muted-foreground">
              Merchant-based capture via adapters — replaces S1.0 + S1.1
            </p>
          </div>
        </div>
        {statusCfg && latestAttempt && (
          <Badge variant={statusCfg.badgeVariant} className="gap-1 text-xs">
            {statusCfg.icon}
            {statusCfg.label}
          </Badge>
        )}
      </div>

      {/* Latest attempt stats */}
      {latestAttempt && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">
              {selectedMerchants > 0
                ? `${latestAttempt.merchants_succeeded}/${selectedMerchants}`
                : latestAttempt.merchants_succeeded}
            </div>
            <div className="text-[10px] text-muted-foreground">Merchants OK</div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{displayedMerchantsFailed}</div>
            <div className="text-[10px] text-muted-foreground">Failed</div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{latestAttempt.pages_total}</div>
            <div className="text-[10px] text-muted-foreground">Pages</div>
          </div>
        </div>
      )}

      {/* CR494: Manifest completeness */}
      {manifestStats && manifestStats.total > 0 && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{manifestStats.expectedPages}</div>
            <div className="text-[10px] text-muted-foreground">Expected</div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{manifestStats.registryPages}</div>
            <div className="text-[10px] text-muted-foreground">
              {manifestStats.registryUsingFallback
                ? `Registry (raw ${manifestStats.registryPagesRaw}, fallback)`
                : "Registry"}
            </div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className={`text-lg font-semibold ${manifestStats.incomplete > 0 ? 'text-destructive' : ''}`}>
              {manifestStats.complete}/{manifestStats.total}
            </div>
            <div className="text-[10px] text-muted-foreground">Manifests OK</div>
          </div>
        </div>
      )}
      {pageSurfaceStats && (
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{pageSurfaceStats.truthRenderedPages}</div>
            <div className="text-[10px] text-muted-foreground">Truth Rendered</div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{pageSurfaceStats.registryPagesEffective}</div>
            <div className="text-[10px] text-muted-foreground">
              {pageSurfaceStats.registryUsingFallback
                ? `Registry Effective (raw ${pageSurfaceStats.registryPagesRaw})`
                : "Registry Effective"}
            </div>
          </div>
          <div className="bg-muted/40 rounded p-2">
            <div className="text-lg font-semibold">{pageSurfaceStats.attemptPagesTotal}</div>
            <div className="text-[10px] text-muted-foreground">Attempt Pages</div>
          </div>
        </div>
      )}

      {/* CR494: Capture incomplete warning */}
      {manifestStats && manifestStats.incomplete > 0 && (
        <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 rounded-md px-3 py-2">
          <XCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
          <span>
            <strong>Capture Incomplete:</strong> {manifestStats.incomplete} manifest(s) failed checksum.
            Expected {manifestStats.expectedPages} pages, registry has {manifestStats.registryPages}.
            S1.2 OCR is blocked until capture is complete.
          </span>
        </div>
      )}

      {/* Error message */}
      {latestAttempt?.error_message && latestAttempt.status === "failed" && (
        <div className="text-xs text-destructive bg-destructive/10 rounded px-3 py-2">
          {latestAttempt.error_message}
        </div>
      )}

      {/* Warning: no capture sources */}
      {latestAttempt?.status === "complete" && latestAttempt.pages_total === 0 && (
        <div className="flex items-start gap-2 text-xs text-warning bg-warning/10 rounded-md px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
          <span>
            No capture-enabled merchant sources found.
            Go to <strong>Image Capture Studio</strong> and assign adapters to merchant sources, then re-run capture.
          </span>
        </div>
      )}

      {/* CR488: Proactive diagnostics banner — stuck/failed/orphan jobs */}
      <ImageCaptureDiagnosticsBanner
        batchRunId={batchRunId}
        onOpenDebugModal={() => setShowDebugModal(true)}
      />

      {/* Timestamp */}
      {latestAttempt?.created_at && (
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground">
            Last attempt:{" "}
            {formatDistanceToNow(new Date(latestAttempt.created_at), { addSuffix: true })}
          </p>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-muted-foreground" onClick={() => setShowDebugModal(true)}>
            <Bug className="h-3 w-3 mr-1" /> Debug
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyLocalCommand("cd python_scheduler\n./venv/bin/python3 scheduler.py", "Merchant listener")}
            className="h-8 text-xs"
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            Merchant listener
          </Button>
          {ocrEngine === "true_paddle" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => launchLocalCommand("./venv/bin/python python_scheduler/paddle_ocr_server.py", "True paddleOCR(local)")}
              className="h-8 text-xs"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              True paddleOCR(local)
            </Button>
          )}
          {latestAttempt && (
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchLatestAttempt}
              disabled={isActivelyRunning}
              className="h-8 text-xs"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Refresh
            </Button>
          )}
          {latestAttempt?.status === "complete" && displayedMerchantsFailed > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRetryFailed}
              disabled={isActivelyRunning}
              className="h-8 text-xs"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Retry Failed
            </Button>
          )}
          {latestAttempt?.status === "complete" && latestAttempt.pages_total > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleForceRerun}
              disabled={isActivelyRunning}
              className="h-8 text-xs"
            >
              Force Re-run
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={handleForceStop}
            disabled={!canForceStop}
            className="h-8 text-xs"
          >
            {stopping ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                Stopping…
              </>
            ) : (
              "Force Stop"
            )}
          </Button>
          <Button
            size="sm"
            onClick={() => handleRunCapture("manual")}
            disabled={isActivelyRunning}
            className="h-8 text-xs"
          >
            {isActivelyRunning ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 mr-1" />
                Run Capture Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* CR488: Debug Modal */}
      <ImageCaptureDebugModal
        open={showDebugModal}
        onOpenChange={setShowDebugModal}
        batchRunId={batchRunId}
      />
    </div>
  );
}
